import * as React from "react"
import {
  ChevronsUpDown,
  Loader2,
  LogOut,
  RotateCcw,
} from "lucide-react"

import {
  Avatar,
  AvatarFallback,
} from "@/components/ui/avatar"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { useAuth } from "@/providers/auth-context"
import { data } from "@/constants/sidebar"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import packageJson from "../../../package.json"
import { cn } from "@/lib/utils"
import { ImageWithFallback } from "../ui/image-with-fallback"

const DropDownMenuContent = ({
  fullName,
  email,
}: {
  fullName: string | null | undefined
  email: string | null | undefined
}) => {
  if (!fullName || !email)
    return <Loader2 className="animate-spin" />
  return (
    <>
      <Avatar className="h-8 w-8 rounded-lg">
        <AvatarFallback className="rounded-lg">
          {" "}
          {fullName?.[0]}
        </AvatarFallback>
      </Avatar>
      <div className="grid flex-1 text-left text-sm leading-tight">
        <span className="truncate font-semibold">
          {fullName}
        </span>
        <span className="truncate text-xs">{email}</span>
      </div>
    </>
  )
}

export const PortalSidebarFooter = () => {
  const navigate = useNavigate()
  const { open: sidebarOpen } = useSidebar()
  const { logout, fullName, email } = useAuth()
  const queryClient = useQueryClient()

  const handleClearCache = () => {
    queryClient.clear() // Clear all queries in the cache
    navigate(0) // Refresh page in react-router
  }
  const handleLogout = async () => {
    await logout()
  }

  return (
    <SidebarFooter>
      <div className="flex items-center justify-center">
        <img
          src={"/p2b_512.png"}
          alt="Shield"
          width={20}
          height={20}
          className={cn(sidebarOpen ? "hidden" : "block")}
        />
        <ImageWithFallback
          src={"/pay2b.svg"}
          alt="Merchant Logo"
          width={80}
          height={80}
          className={cn(
            "w-auto place-self-center",
            sidebarOpen ? "opacity-100" : "hidden"
          )}
        />
      </div>
      <div
        className={cn(
          "text-center text-xs text-muted-foreground",
          !sidebarOpen && "hidden"
        )}
      >
        v.{packageJson.version}
      </div>

      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="hover:bg-gray-200 hover:text-black data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <DropDownMenuContent
                  fullName={fullName}
                  email={email}
                />
                <ChevronsUpDown className="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              side="bottom"
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <DropDownMenuContent
                    fullName={fullName}
                    email={email}
                  />
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />

              <DropdownMenuItem
                className="cursor-pointer"
                onClick={handleClearCache}
              >
                <RotateCcw />
                {"Limpar Cache"}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(event) => {
                  event.preventDefault()
                  handleLogout()
                }}
                className="cursor-pointer"
              >
                <LogOut />
                {data.logoutText}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarFooter>
  )
}
